import { useMemo, useState } from 'react';
import { RefreshCw } from 'lucide-react';
import { Button, Skeleton, Progress, Chip } from '@vnetwork/ui/primitives';
import { BarList, EmptyState, DataTable } from '@vnetwork/ui/data';
import { PieChart, Chart } from '@vnetwork/ui/charts';
import { ConfirmDialog } from '@vnetwork/ui/feedback';
import { useCloudCapacity, useTriggerSync, useSyncJobs, type CloudHost } from '@/api/capacities-cloud';
import { CapacitySectionCard } from './capacity-section-card';
import { CloudStatCards } from './cloud-stat-cards';
import { pct1, usageColor, usageTone, formatGb } from './capacity-format';
import { CloudMappingSection } from '../cloud/CloudMappingSection';
import { zoneListColumns } from '../cloud/zone-column-definitions';

const GRID_2: React.CSSProperties = {
  display: 'grid',
  gap: 'var(--vn-space-4)',
  gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
};

/** Top-N hosts sorted desc by a usage metric, as BarList items with threshold colors. */
function hostBars(hosts: CloudHost[], metric: 'cpu_usage_pct' | 'memory_usage_pct', limit = 10) {
  return [...hosts]
    .sort((a, b) => (b[metric] ?? 0) - (a[metric] ?? 0))
    .slice(0, limit)
    .map((h) => {
      const value = pct1(h[metric]);
      return { label: h.name, value, display: `${value}%`, color: usageColor(value) };
    });
}

/**
 * Cloud Resources tab — ServiceMgmt compute/storage/network capacity. Stat
 * cards, a compute-zone table (drill into hosts/datastores/instances), per-host
 * CPU/memory usage bars, storage overview charts, and equipment mappings. The
 * "Sync Now" button triggers a live ServiceMgmt sync behind a confirm dialog.
 */
export function CloudResourcesTab() {
  const { data, isLoading, isError } = useCloudCapacity();
  const triggerSync = useTriggerSync();
  const syncJobs = useSyncJobs();
  const [confirmOpen, setConfirmOpen] = useState(false);

  // Last completed sync job — used to show last-sync timestamp + status badge.
  const lastJob = syncJobs.data?.[0] as Record<string, unknown> | undefined;
  const lastSyncAt = lastJob?.completed_at ?? lastJob?.created_at ?? lastJob?.updated_at;
  const lastSyncStatus = lastJob?.status as string | undefined;

  const cpuBars = useMemo(() => hostBars(data?.compute.hosts ?? [], 'cpu_usage_pct'), [data]);
  const memoryBars = useMemo(() => hostBars(data?.compute.hosts ?? [], 'memory_usage_pct'), [data]);

  const storageSlices = useMemo(() => {
    const s = data?.storage;
    if (!s) return [];
    return [
      { name: 'Used', value: Math.max(0, s.used_gb), color: usageColor(pct1(s.usage_pct)) },
      { name: 'Free', value: Math.max(0, s.free_gb), color: 'var(--vn-fg-subtle)' },
    ];
  }, [data]);

  const datastoreRows = useMemo(() => {
    const rows = data?.storage.datastores ?? [];
    return [...rows]
      .sort((a, b) => (b.capacity_gb ?? 0) - (a.capacity_gb ?? 0))
      .slice(0, 10)
      .map((d) => ({ label: d.name, used_gb: Math.max(0, d.used_gb), free_gb: Math.max(0, d.free_gb) }));
  }, [data]);

  const syncStatusTone: 'success' | 'danger' | 'warning' | 'neutral' =
    lastSyncStatus === 'completed' ? 'success'
    : lastSyncStatus === 'failed' ? 'danger'
    : lastSyncStatus ? 'warning'
    : 'neutral';

  const syncButton = (
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--vn-space-2)', flexWrap: 'wrap' }}>
      {lastSyncAt ? (
        <span style={{ fontSize: 'var(--vn-font-size-xs)', color: 'var(--vn-fg-muted)' }}>
          Last sync:{' '}
          {new Date(lastSyncAt as string).toLocaleString()}
          {lastSyncStatus ? (
            <Chip tone={syncStatusTone} style={{ marginLeft: 'var(--vn-space-1)' }}>
              {lastSyncStatus}
            </Chip>
          ) : null}
        </span>
      ) : null}
      <Button
        variant="secondary"
        size="sm"
        onClick={() => setConfirmOpen(true)}
        disabled={triggerSync.isPending}
        title="Trigger a ServiceMgmt capacity sync"
      >
        <RefreshCw size={14} /> Sync Now
      </Button>
    </div>
  );

  const syncConfirm = (
    <ConfirmDialog
      open={confirmOpen}
      onOpenChange={setConfirmOpen}
      title="Sync cloud capacity?"
      description="This triggers a fresh capacity sync from the compute providers. It may take a few minutes to complete."
      confirmLabel="Sync Now"
      loading={triggerSync.isPending}
      onConfirm={() =>
        triggerSync.mutate(undefined, { onSettled: () => setConfirmOpen(false) })
      }
    />
  );

  if (isError) {
    return (
      <EmptyState
        title="Failed to load cloud resources"
        description="Cloud capacity data is unavailable right now."
      />
    );
  }

  if (isLoading || !data) {
    return (
      <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
        <Skeleton style={{ height: 110 }} />
        <Skeleton style={{ height: 280 }} />
      </div>
    );
  }

  const { summary, storage } = data;
  const usagePct = pct1(storage.usage_pct);

  // `summary.total_zones` counts ALL provider zones (cdn/streaming/registrar/…);
  // this compute-capacity view must report only compute zones. The backend no
  // longer honours the `provider_type=compute` filter, so derive it client-side.
  const baseZones = (data.by_zone ?? []).filter((z) => z.provider_type === 'compute');
  const healthyZones = baseZones.filter((z) => z.status === 'healthy').length;
  const zoneCount = data.by_zone?.length ? baseZones.length : summary.total_zones;

  // Legacy shows per-zone Hosts/VMs/CPU/Memory/Storage — derive them from the
  // hosts + datastores lists grouped by zone name (the zone rows don't carry them).
  const hosts = data.compute.hosts ?? [];
  const datastores = data.storage.datastores ?? [];
  const computeZones = baseZones.map((z) => {
    const zHosts = hosts.filter((h) => h.zone_name === z.zone_name);
    const zStores = datastores.filter((d) => d.zone_name === z.zone_name);
    const avg = (xs: number[]) => (xs.length ? xs.reduce((s, n) => s + n, 0) / xs.length : 0);
    return {
      ...z,
      host_count: zHosts.length,
      vm_count: zHosts.reduce((s, h) => s + (h.vm_count ?? 0), 0),
      cpu_usage_pct: avg(zHosts.map((h) => h.cpu_usage_pct ?? 0)),
      memory_usage_pct: avg(zHosts.map((h) => h.memory_usage_pct ?? 0)),
      storage_gb: zStores.reduce((s, d) => s + (d.capacity_gb ?? 0), 0),
    };
  });

  return (
    <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
      {syncConfirm}

      <CloudStatCards
        zoneCount={zoneCount}
        healthyZones={healthyZones}
        totalHosts={summary.total_hosts}
        healthyHosts={summary.healthy_hosts}
        totalVms={summary.total_vms}
        cpuPct={pct1(summary.cpu_usage_pct)}
        memoryPct={pct1(summary.memory_usage_pct)}
        storagePct={pct1(summary.storage_usage_pct)}
      />

      <CapacitySectionCard title="Compute Zones" description="Drill into a zone's hosts, datastores and instances">
        <DataTable
          columns={zoneListColumns}
          data={computeZones}
          getRowId={(z) => z.zone_id}
          emptyState={<EmptyState size="sm" title="No compute zones" />}
        />
      </CapacitySectionCard>

      <div style={GRID_2}>
        <CapacitySectionCard title="CPU Usage by Host" description="Top hosts by CPU utilization">
          {cpuBars.length > 0 ? <BarList items={cpuBars} max={100} /> : <EmptyState size="sm" title="No hosts" />}
        </CapacitySectionCard>

        <CapacitySectionCard title="Memory Usage by Host" description="Top hosts by memory utilization">
          {memoryBars.length > 0 ? (
            <BarList items={memoryBars} max={100} />
          ) : (
            <EmptyState size="sm" title="No hosts" />
          )}
        </CapacitySectionCard>
      </div>

      <div style={GRID_2}>
        <CapacitySectionCard
          title="Storage Overview"
          description={`${formatGb(storage.used_gb)} used of ${formatGb(storage.total_capacity_gb)}`}
        >
          {storageSlices.length > 0 && storage.total_capacity_gb > 0 ? (
            <PieChart
              donut
              height={240}
              data={storageSlices}
              valueFormatter={(v) => formatGb(typeof v === 'number' ? v : Number(v) || 0)}
              centerLabel={
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 'var(--vn-font-size-2xl)', fontWeight: 'var(--vn-font-weight-semibold)' }}>
                    {usagePct}%
                  </div>
                  <div style={{ fontSize: 'var(--vn-font-size-xs)', color: 'var(--vn-fg-muted)' }}>Used</div>
                </div>
              }
            />
          ) : (
            <EmptyState size="sm" title="No storage data" />
          )}
          <div style={{ marginTop: 'var(--vn-space-4)' }}>
            <Progress value={usagePct} tone={usageTone(usagePct)} size="lg" showValue label="Storage utilization" />
          </div>
        </CapacitySectionCard>

        <CapacitySectionCard title="Storage by Datastore" description="Used vs free capacity per datastore">
          {datastoreRows.length > 0 ? (
            <Chart
              type="bar"
              stacked
              height={280}
              data={datastoreRows}
              xKey="label"
              series={[
                { key: 'used_gb', label: 'Used', color: 'var(--vn-primary-solid)' },
                { key: 'free_gb', label: 'Free', color: 'var(--vn-fg-subtle)' },
              ]}
              showLegend
              valueFormatter={(v) => formatGb(typeof v === 'number' ? v : Number(v) || 0)}
            />
          ) : (
            <EmptyState size="sm" title="No datastores" />
          )}
        </CapacitySectionCard>
      </div>

      <CapacitySectionCard
        title="Equipment Mappings"
        description="Provider-to-host mappings synced from the compute service"
        actions={syncButton}
      >
        <CloudMappingSection />
      </CapacitySectionCard>
    </div>
  );
}
